/** The setup wizard's step map — which preflight items each step owns.
 *
 *  A step is a PRESENTATION over the per-TO checklist, never its own state
 *  machine: statuses derive from the owned items on every poll. An action not
 *  claimed here degrades gracefully — its config fields surface on the
 *  Parameters step and its row on the Review & run board — so a new backend
 *  action never breaks the wizard; claim it here when it earns a dedicated
 *  step.
 */
import type { PreflightItem } from '@/api/preflight';

export type SetupSection = 'create' | 'configure' | 'data' | 'run';

/** Rail glyph vocabulary (one per status; never color alone — tooltips carry text). */
export type SetupStepStatus =
  | 'done' // every applicable owned item completed
  | 'running' // an owned item is mid-run
  | 'error' // an owned item errored
  | 'attention' // an owned item needs ops input (not ready)
  | 'blocked' // owned items wait on unmet dependencies
  | 'ready' // actionable now
  | 'edit'; // an always-editable review step (profile)

export interface SetupStepDef {
  id: string;
  label: string;
  section: SetupSection;
  /** Actions this step hosts (empty = derived elsewhere: profile/parameters/run). */
  itemActions: readonly string[];
}

export const SETUP_STEPS: readonly SetupStepDef[] = [
  { id: 'basics', label: 'Basics', section: 'create', itemActions: [] },
  { id: 'admin', label: 'Admin user', section: 'create', itemActions: [] },
  // A partner's sandbox (metaplan G1): the contract first — every other item waits on it —
  // then the sample data and the hand-over. Hidden on every other tenant (no applicable item).
  {
    id: 'partner',
    label: 'Partner',
    section: 'configure',
    itemActions: ['PARTNER_CONTRACT', 'SANDBOX_SEED', 'PARTNER_HANDOVER'],
  },
  { id: 'profile', label: 'Profile & features', section: 'configure', itemActions: [] },
  { id: 'parameters', label: 'Parameters', section: 'configure', itemActions: [] },
  {
    id: 'access',
    label: 'Access & team',
    section: 'configure',
    itemActions: ['ACCESS_GROUPS_SETUP', 'TEAM_USERS'],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    section: 'configure',
    itemActions: ['NOTIFICATION_PACK', 'RESELLER_POLICY_COMPILE', 'REVIEW_CONFIG'],
  },
  { id: 'catalog', label: 'Catalog sync', section: 'data', itemActions: ['CATALOG_SYNC'] },
  // The live booking feed (Ventrata's notification subscriptions): after the catalogue the
  // bookings match tours from, before the backfill that depends on it. A manual execute —
  // it registers subscriptions at the vendor, so it never autoruns.
  { id: 'feed', label: 'Booking feed', section: 'data', itemActions: ['NOTIFICATION_SUBSCRIPTIONS'] },
  // The backfill form first; the first-webhook verify row renders beneath it as a
  // generic item panel (the data steps host any further owned item that way).
  {
    id: 'bookings',
    label: 'Booking backfill',
    section: 'data',
    itemActions: ['BOOKING_BACKFILL', 'RESTECH_WEBHOOK_RECEIVED'],
  },
  { id: 'auxinfo', label: 'Auxiliary info', section: 'data', itemActions: ['AUX_INFO_SYNC'] },
  { id: 'run', label: 'Review & run', section: 'run', itemActions: [] },
] as const;

export type SetupStepId = (typeof SETUP_STEPS)[number]['id'];

export const SECTION_LABELS: Record<SetupSection, string> = {
  create: 'Create',
  configure: 'Configure',
  data: 'Data',
  run: 'Run',
};

/** Every action a dedicated step owns (the Parameters step excludes these). */
export const OWNED_ACTIONS: ReadonlySet<string> = new Set(
  SETUP_STEPS.flatMap((s) => [...s.itemActions]),
);

/** The Parameters step's population: applicable items that take ops input via
 *  config fields and have no dedicated step of their own. */
export function parameterItems(items: PreflightItem[]): PreflightItem[] {
  return items.filter(
    (i) =>
      i.applicable &&
      !OWNED_ACTIONS.has(i.action) &&
      (i.input_kind === 'required' || i.input_kind === 'optional') &&
      i.config_fields.length > 0 &&
      // Not while a prerequisite is unmet. "Enable soft-launch notifications"
      // is a go-live switch gated on the notification pack existing; showing it
      // on the day-one Parameters screen under "Waiting on Materialise standard
      // notifications" asks the operator to fill in something they must not act
      // on yet. It reappears here the moment it is actionable.
      i.blocked_by.length === 0,
  );
}
